import { Box, Tooltip, Typography, tooltipClasses } from "@mui/material";
import { Flex } from "Components/_Shared/Core/Flex";
import { UserInitialsAvatar } from "Components/_Shared/UserInitialsAvatar";
import { type AnalysisOverviewClient, AnalysisOverviewClientRelationType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatFamilyMembersCount } from "Infrastructure/Utils/LanguageUtils";
import { useState } from "react";

type Props = {
  readonly clientRelations: AnalysisOverviewClient[];
};

const isClient = (member: AnalysisOverviewClient) => member.relationType === AnalysisOverviewClientRelationType.Client;

const getFullName = (member: AnalysisOverviewClient) => [member.firstName, member.surname].filter(Boolean).join(" ");

// the same person can be listed under more than one relation type - keep them once, as the client if they are one
const getFamilyMembers = (clientRelations: AnalysisOverviewClient[]) => {
  const clientFirst = [...clientRelations.filter(isClient), ...clientRelations.filter((member) => !isClient(member))];
  return clientFirst.filter((member, index) => clientFirst.findIndex((m) => m.guid === member.guid) === index);
};

export const AnalysesFamilyMembersCell: React.FC<Props> = ({ clientRelations }) => {
  const { t } = useResource();
  const [isOpen, setIsOpen] = useState(false);

  const members = getFamilyMembers(clientRelations);
  const countLabel = formatFamilyMembersCount(members.length, t);

  if (members.length <= 1) {
    return (
      <Typography variant="body2" whiteSpace="nowrap" sx={(theme) => ({ color: theme.colors.darkGrey })}>
        {countLabel}
      </Typography>
    );
  }

  return (
    // the tooltip is portaled, but React still bubbles its clicks (and a tap on the count) up to the row, which would open the detail
    <Box onClick={(event) => event.stopPropagation()}>
      <Tooltip
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        placement="bottom-start"
        enterTouchDelay={0}
        describeChild
        slotProps={{
          tooltip: {
            sx: {
              width: 270,
              maxWidth: "none",
              py: "14px",
              px: Spacing.XL,
              bgcolor: "common.white",
              border: 1,
              borderColor: "divider",
              borderRadius: "14px",
              boxShadow: 3,
              [`.${tooltipClasses.popper}[data-popper-placement*="bottom"] &`]: { mt: Spacing.M },
            },
          },
        }}
        title={
          <>
            <Typography fontSize={13} color="text.secondary" mb={Spacing.XS}>
              {t(Resources.AnalysesListing.Table.FamilyMembers.Title)}
            </Typography>
            <Box component="ul" m={0} p={0} sx={{ listStyle: "none" }}>
              {members.map((member, index) => {
                const fullName = getFullName(member);
                const isMemberClient = isClient(member);

                return (
                  <Flex
                    component="li"
                    key={member.guid}
                    alignItems="center"
                    gap={Spacing.M}
                    py="6px"
                    borderTop={index > 0 ? 1 : 0}
                    borderColor="divider"
                  >
                    <UserInitialsAvatar
                      variant="xs"
                      color={isMemberClient ? "primary" : "secondary"}
                      firstName={member.firstName}
                      surname={member.surname}
                    />
                    <Typography fontSize={14} fontWeight={500}>
                      {fullName}
                    </Typography>
                    {isMemberClient && (
                      <Typography fontSize={12} color="text.secondary" ml="auto">
                        {t(Resources.AnalysesListing.Table.FamilyMembers.Client)}
                      </Typography>
                    )}
                  </Flex>
                );
              })}
            </Box>
          </>
        }
      >
        <Typography
          component="button"
          type="button"
          variant="body2"
          whiteSpace="nowrap"
          p={0}
          border="none"
          bgcolor="transparent"
          sx={(theme) => ({
            color: isOpen ? "primary.main" : theme.colors.darkGrey,
            textDecoration: "underline dotted",
            textUnderlineOffset: "4px",
            "&:hover": { color: "primary.main" },
          })}
        >
          {countLabel}
        </Typography>
      </Tooltip>
    </Box>
  );
};
